import { useMemo } from "react";
import VerticalSpacingContext from "src/lib/context/VerticalSpacingContext";

import { IPageMetadataFields } from "../../../@types/generated/contentful";
import { CollectionListSection, RenderSections } from "../../lib/components";
import promoteH1RenderSectionMiddleware from "../../lib/components/RenderSections/middleware/promoteH1RenderSectionMiddleware";
import {
  FormattedAd,
  FormattedCollection,
} from "../../lib/queries/data/collectionData";
import { QueryData } from "../../lib/queries/server";
import {
  composePaths,
  composeProps,
  PageProps,
} from "../../lib/services/util/compose";
import { isTruthy } from "../../lib/util";
import { filterListByLocales, LocaleCode } from "../../lib/util/locale";
import {
  makePageDescription,
  makePageTitle,
} from "../../lib/util/makePageMeta";

export const getStaticPaths = composePaths(async (context, { queryClient }) => {
  const paths = await queryClient.fetchQuery<QueryData<"collectionPaths">>([
    "collectionPaths",
  ]);

  const configuredLocales = context.locales as LocaleCode[] | undefined;

  return {
    paths:
      paths
        .flatMap((path) => {
          const allLocales = Object.keys(path || {});
          const locales = filterListByLocales(allLocales, configuredLocales);
          return locales.map((locale) => {
            const slug = path[locale];
            if (!slug) return undefined;
            return { params: { slug }, locale };
          });
        })
        .filter(isTruthy) ?? [],
    fallback: "blocking",
  };
});

export const getStaticProps = composeProps(async (context, { queryClient }) => {
  const data = await queryClient.fetchQuery<QueryData<"collectionData">>([
    "collectionData",
    context.params?.slug,
    context.locale,
    Boolean(context.preview),
  ]);

  if (!data) {
    return {
      notFound: true,
    };
  }

  const tokens = { category: data.title };
  const backupTitle = makePageTitle(tokens);
  const backupDescription = makePageDescription();

  // instantiate empty object if missing
  if (!data.pageMetadata) data.pageMetadata = {} as IPageMetadataFields;

  // generate title if missing
  if (!data.pageMetadata.title) {
    data.pageMetadata.title = backupTitle;
    data.pageMetadata.appendSiteTitle = false;
  }

  // generate description if missing
  if (!data.pageMetadata.description) {
    data.pageMetadata.description = backupDescription;
  }

  return {
    props: data,
    // daily
    revalidate: 86400,
  };
});
export interface CollectionProps {
  title: string;
  topSections: React.ComponentProps<typeof RenderSections>["sections"];
  sections: React.ComponentProps<typeof RenderSections>["sections"];
  bottomSections: React.ComponentProps<typeof RenderSections>["sections"];
  ads: FormattedAd[];
  layoutStyle: string;
  items: FormattedCollection["items"];
}

export default function Collection({
  topSections,
  sections,
  bottomSections,
  ads,
  layoutStyle,
  items = [],
}: PageProps<CollectionProps>) {
  const shouldShowFullGrid = Boolean(items.length) && layoutStyle === "grid";
  const verticalSpacing = useMemo(() => ({ hasVerticalSpacing: false }), []);
  return (
    <VerticalSpacingContext.Provider value={verticalSpacing}>
      <RenderSections
        sections={promoteH1RenderSectionMiddleware(
          sections.length ? sections : topSections
        )}
      />
      {shouldShowFullGrid && (
        <div className="mx-6 my-5">
          <CollectionListSection content={items} ads={ads} />
        </div>
      )}
      <RenderSections sections={bottomSections} />
    </VerticalSpacingContext.Provider>
  );
}
